import React, { useMemo } from 'react';
import { LegacyCptReport } from '../../../services/legacyCptReportService';
import { getLegacyCptCodeLabels } from './legacyCptReportUtils';
import { PreviewShell } from '../../preview/PreviewShell';
import {
    PreviewField,
    PreviewRichText,
    PreviewSection,
    type PreviewFieldData,
    type PreviewSectionData,
    type PreviewSubsectionData,
} from '@nordinsight/implant-core/ui';

interface LegacyCptReportPreviewProps {
    report: LegacyCptReport;
    className?: string;
    onClose?: () => void;
}

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

const normalizePreviewString = (
    value: string | null | undefined
): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
};

const formatOptionalDate = (
    value: string | null | undefined
): string | null => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime())
        ? null
        : parsedDate.toLocaleDateString();
};

const formatOptionalMinutes = (
    value: number | null | undefined
): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return `${value} minute${value === 1 ? '' : 's'}`;
};

const formatOptionalList = (items: string[]): string | null => {
    const normalizedItems = items
        .map((item) => normalizePreviewString(item))
        .filter((item): item is string => !!item);
    return normalizedItems.length ? normalizedItems.join(', ') : null;
};

const renderTextValue = (
    value: string | null | undefined
): React.ReactNode | null => {
    const normalizedValue = normalizePreviewString(value);
    if (!normalizedValue) return null;

    if (HTML_TAG_PATTERN.test(normalizedValue)) {
        return <PreviewRichText html={normalizedValue} />;
    }

    return <span className="whitespace-pre-wrap">{normalizedValue}</span>;
};

const renderListValue = (items: string[]): React.ReactNode | null => {
    const formattedItems = formatOptionalList(items);
    return formattedItems ? (
        <span className="whitespace-pre-wrap">{formattedItems}</span>
    ) : null;
};

const LegacyCptReportPreview: React.FC<LegacyCptReportPreviewProps> = ({
    report,
    className,
    onClose,
}) => {
    const codeLabels = useMemo(
        () =>
            getLegacyCptCodeLabels(report).map((code) =>
                code.toUpperCase().startsWith('CPT ')
                    ? code.toUpperCase()
                    : `CPT ${code}`
            ),
        [report]
    );

    const metadataFields = useMemo<PreviewFieldData[]>(
        () => [
            {
                key: 'assessmentDate',
                label: 'Assessment date',
                value: formatOptionalDate(report.info.dateOfAssessment),
            },
            {
                key: 'serviceDate',
                label: 'Date of service',
                value: formatOptionalDate(report.info.dateOfService),
            },
        ],
        [report.info.dateOfAssessment, report.info.dateOfService]
    );

    const hasApprovalContent = useMemo(
        () =>
            report.approval.approved ||
            !!formatOptionalDate(report.approval.date) ||
            !!normalizePreviewString(report.approval.byName) ||
            !!normalizePreviewString(report.approval.notes),
        [
            report.approval.approved,
            report.approval.date,
            report.approval.byName,
            report.approval.notes,
        ]
    );

    const sections = useMemo<PreviewSectionData[]>(
        () => [
            {
                key: 'metadata',
                title: 'Report Metadata and Info',
                fields: [
                    {
                        key: 'name',
                        label: 'Name',
                        value: renderTextValue(report.info.name),
                    },
                    {
                        key: 'facility',
                        label: 'Facility',
                        value: renderTextValue(report.info.facilityName),
                    },
                    {
                        key: 'purpose',
                        label: 'Purpose',
                        value: renderTextValue(report.info.purpose),
                    },
                    {
                        key: 'clinicalIndication',
                        label: 'Clinical indication',
                        value: renderTextValue(report.info.clinicalIndication),
                    },
                    {
                        key: 'sources',
                        label: 'Sources',
                        value: renderListValue(report.info.sources),
                    },
                    {
                        key: 'notes',
                        label: 'Notes',
                        value: renderTextValue(report.info.notes),
                    },
                ],
            },
            {
                key: 'safetyWork',
                title: 'Safety Work',
                fields: [
                    {
                        key: 'purpose',
                        label: 'Purpose',
                        value: renderTextValue(report.safetyWork.purpose),
                    },
                    {
                        key: 'objectives',
                        label: 'Objectives',
                        value: renderTextValue(report.safetyWork.objectives),
                    },
                    {
                        key: 'clinicalIndication',
                        label: 'Clinical indication',
                        value: renderTextValue(
                            report.safetyWork.clinicalIndication
                        ),
                    },
                ],
            },
            {
                key: 'mriSafetyDecision',
                title: 'MRI Safety Decision Details',
                subsections: [
                    {
                        key: 'implantDetails',
                        title: 'Implant Details',
                        fields: [
                            {
                                key: 'model',
                                label: 'Model',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.implantDetails
                                        .model
                                ),
                            },
                            {
                                key: 'catalogNumber',
                                label: 'Catalog number',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.implantDetails
                                        .catalogNumber
                                ),
                            },
                            {
                                key: 'type',
                                label: 'Type',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.implantDetails
                                        .type
                                ),
                            },
                            {
                                key: 'manufacturer',
                                label: 'Manufacturer',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.implantDetails
                                        .manufacturer
                                ),
                            },
                        ],
                    },
                    {
                        key: 'foreignBodyDetails',
                        title: 'Foreign Body Details',
                        fields: [
                            {
                                key: 'type',
                                label: 'Type',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.type
                                ),
                            },
                            {
                                key: 'location',
                                label: 'Location',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.location
                                ),
                            },
                            {
                                key: 'description',
                                label: 'Description',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.description
                                ),
                            },
                            {
                                key: 'material',
                                label: 'Material',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.material
                                ),
                            },
                            {
                                key: 'dimensions',
                                label: 'Dimensions',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.dimensions
                                ),
                            },
                            {
                                key: 'hazards',
                                label: 'Hazards',
                                value: renderListValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.hazards
                                ),
                            },
                            {
                                key: 'hazardExplanation',
                                label: 'Hazard explanation',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo
                                        .foreignBodyDetails.hazardExplanation
                                ),
                            },
                        ],
                    },
                    {
                        key: 'safetyAnalysis',
                        title: 'Safety Analysis',
                        fields: [
                            {
                                key: 'risk',
                                label: 'Risk',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .risk
                                ),
                            },
                            {
                                key: 'mitigation',
                                label: 'Mitigation',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .mitigation
                                ),
                            },
                            {
                                key: 'limitSar',
                                label: 'Limit SAR',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .limitSar
                                ),
                            },
                            {
                                key: 'sequenceMod',
                                label: 'Sequence modifications',
                                value: renderTextValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .sequenceMod
                                ),
                            },
                            {
                                key: 'additionalMod',
                                label: 'Additional modifications',
                                value: renderListValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .additionalMod
                                ),
                            },
                            {
                                key: 'staffNeeded',
                                label: 'Staff needed',
                                value: renderListValue(
                                    report.mriSafetyDecisionInfo.safetyAnalysis
                                        .staffNeeded
                                ),
                            },
                        ],
                    },
                ] as PreviewSubsectionData[],
            },
            {
                key: 'conditionalsAnalysis',
                title: 'Conditionals and Benefits Analysis',
                fields: [
                    {
                        key: 'mriStatus',
                        label: 'MRI status',
                        value: renderTextValue(
                            report.conditionalsAnalysis.mriStatus
                        ),
                    },
                    {
                        key: 'riskLevel',
                        label: 'Risk level',
                        value: renderTextValue(
                            report.conditionalsAnalysis.riskLevel
                        ),
                    },
                    {
                        key: 'restrictions',
                        label: 'Restrictions',
                        value: renderTextValue(
                            report.conditionalsAnalysis.restrictions
                        ),
                    },
                    {
                        key: 'hazards',
                        label: 'Hazards',
                        value: renderListValue(
                            report.conditionalsAnalysis.hazards
                        ),
                    },
                    {
                        key: 'benefits',
                        label: 'Benefits',
                        value: renderTextValue(
                            report.conditionalsAnalysis.benefits.benefits
                        ),
                    },
                    {
                        key: 'conclusion',
                        label: 'Conclusion',
                        value: renderTextValue(
                            report.conditionalsAnalysis.benefits.conclusion
                        ),
                    },
                    {
                        key: 'explanation',
                        label: 'Explanation',
                        value: renderTextValue(
                            report.conditionalsAnalysis.benefits.explanation
                        ),
                    },
                ],
            },
            {
                key: 'guidance',
                title: 'Guidance',
                fields: [
                    {
                        key: 'sopName',
                        label: 'SOP name',
                        value: renderTextValue(report.guidance.sopName),
                    },
                    {
                        key: 'sopTitle',
                        label: 'SOP title',
                        value: renderTextValue(report.guidance.sopInfo.title),
                    },
                    {
                        key: 'sopAuthor',
                        label: 'SOP author',
                        value: renderTextValue(report.guidance.sopInfo.author),
                    },
                    {
                        key: 'sopDate',
                        label: 'SOP date',
                        value: formatOptionalDate(report.guidance.sopInfo.date),
                    },
                    {
                        key: 'guidanceSources',
                        label: 'Sources',
                        value: renderListValue(report.guidance.sources),
                    },
                    {
                        key: 'guidanceExplanation',
                        label: 'Explanation',
                        value: renderTextValue(report.guidance.explanation),
                    },
                    {
                        key: 'recommendation',
                        label: 'Recommendation',
                        value: renderTextValue(report.guidance.recommendation),
                    },
                    {
                        key: 'guidanceNotes',
                        label: 'Notes',
                        value: renderTextValue(report.guidance.notes),
                    },
                ],
            },
            {
                key: 'scannerSettings',
                title: 'Scanner Settings',
                fields: [
                    {
                        key: 'scannerModel',
                        label: 'Scanner model',
                        value: renderTextValue(
                            report.scannerSettings.scannerModel
                        ),
                    },
                    {
                        key: 'technique',
                        label: 'Technique',
                        value: renderTextValue(
                            report.scannerSettings.technique
                        ),
                    },
                    {
                        key: 'patientPosition',
                        label: 'Patient position',
                        value: renderTextValue(
                            report.scannerSettings.patientPosition
                        ),
                    },
                ],
            },
            {
                key: 'summary',
                title: 'Summary and CPT Codes',
                fields: [
                    {
                        key: 'cptCodes',
                        label: 'CPT codes',
                        value: codeLabels.length ? codeLabels.join(', ') : null,
                    },
                    {
                        key: 'totalTime',
                        label: 'Total time',
                        value: formatOptionalMinutes(
                            report.summary.totalTimeCPTCodes
                        ),
                    },
                    {
                        key: 'count15',
                        label: 'CPT 76015 count',
                        value:
                            typeof report.summary.count15 === 'number'
                                ? String(report.summary.count15)
                                : null,
                    },
                    {
                        key: 'summaryNotes',
                        label: 'Summary notes',
                        value: renderTextValue(report.summary.notes),
                    },
                ],
            },
            {
                key: 'approval',
                title: 'Approval Metadata',
                fields: hasApprovalContent
                    ? [
                          {
                              key: 'approved',
                              label: 'Approved',
                              value: report.approval.approved ? 'Yes' : 'No',
                          },
                          {
                              key: 'approvedBy',
                              label: 'Approved by',
                              value: renderTextValue(report.approval.byName),
                          },
                          {
                              key: 'approvalDate',
                              label: 'Approval date',
                              value: formatOptionalDate(report.approval.date),
                          },
                          {
                              key: 'approvalNotes',
                              label: 'Approval notes',
                              value: renderTextValue(report.approval.notes),
                          },
                      ]
                    : [],
            },
            {
                key: 'timeLog',
                title: 'Detailed Time Log',
                fields: report.timeLog
                    .map((item, index) => {
                        const taskLabel = normalizePreviewString(item.task);
                        const timeValue = formatOptionalMinutes(item.time);

                        if (taskLabel && timeValue) {
                            return {
                                key: `timeLog-${index}`,
                                label: taskLabel,
                                value: timeValue,
                            };
                        }
                        if (taskLabel) {
                            return {
                                key: `timeLog-${index}`,
                                label: `Entry ${index + 1}`,
                                value: taskLabel,
                            };
                        }
                        if (timeValue) {
                            return {
                                key: `timeLog-${index}`,
                                label: `Entry ${index + 1}`,
                                value: timeValue,
                            };
                        }
                        return {
                            key: `timeLog-${index}`,
                            label: `Entry ${index + 1}`,
                            value: null,
                        };
                    })
                    .filter(
                        (field) =>
                            field.value !== null && field.value !== undefined
                    ),
            },
        ],
        [codeLabels, hasApprovalContent, report]
    );

    const hasPreviewContent =
        metadataFields.some(
            (f) =>
                f.value !== null &&
                f.value !== undefined &&
                f.value !== '' &&
                f.value !== false
        ) || sections.length > 0;

    return (
        <PreviewShell
            className={className}
            hasContent={hasPreviewContent}
            onClose={onClose}
            previewSource="cpt"
        >
            {metadataFields.some(
                (f) =>
                    f.value !== null &&
                    f.value !== undefined &&
                    f.value !== '' &&
                    f.value !== false
            ) && (
                <div className="mb-6 break-inside-avoid">
                    {metadataFields.map((field) => (
                        <PreviewField key={field.key} field={field} />
                    ))}
                </div>
            )}

            {sections.map((section) => (
                <PreviewSection key={section.key} data={section} />
            ))}
        </PreviewShell>
    );
};

export default LegacyCptReportPreview;
